import { AnyAction } from '@reduxjs/toolkit';
import {
  assistRequestAssigned,
  isAssistTooLate,
} from '../../redux/features/assistRequest/assistRequest.slice';
import {
  AssistPoolData,
  ASSIST_SCHEMA_VERSION,
  IAssistDataBase,
  IAssistEnvelope,
} from './types';

/**
 * HITL-1792 (HITL 8.0.0): task-router's assist pool events, as they arrive on
 * the agent's existing `TASK_CHANNEL_<user id>` socket (contract §4.4).
 *
 * Pure: `routeAssistPoolEvent` turns one parsed message into the slice action
 * to dispatch, or says why it was ignored. The hook owns the socket, the clock
 * and the logging.
 */

export const AssistPoolEvents = {
  assigned: 'assist_request_assigned',
} as const;

/**
 * The types this console has a card body for. An assignment of any other
 * type is ignored (contract D5): task-router may register a type before this
 * console ships its card.
 */
export const RENDERABLE_ASSIST_TYPES: ReadonlySet<string> = new Set([
  'hear_and_confirm',
]);

const SCHEMA_MAJOR = ASSIST_SCHEMA_VERSION.split('.')[0];

const isObject = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isHearing = (value: unknown) =>
  isObject(value) &&
  typeof value.text === 'string' &&
  (value.nbest === undefined ||
    value.nbest === null ||
    Array.isArray(value.nbest));

/**
 * Whether the card can render `request` without throwing (review B1.8): a
 * throw in the card is the error page, and the task socket unmounts with it.
 * Checks the shape the card reads, nothing more.
 */
const isRenderableRequest = (type: string, request: unknown): boolean => {
  if (!isObject(request)) return false;
  const { context } = request;
  if (context !== undefined && context !== null) {
    if (!isObject(context)) return false;
    if (context.transcript != null && !Array.isArray(context.transcript)) {
      return false;
    }
    if (context.cart != null && !Array.isArray(context.cart)) return false;
  }
  if (type === 'hear_and_confirm') {
    const { body } = request;
    return (
      isObject(body) && isHearing(body.primary) && isHearing(body.secondary)
    );
  }
  return true;
};

/** Same major as ours; a newer minor only adds optional fields. */
/** Where `event` sits in every assist envelope the bus sends: before `data`. */
const ASSIST_FRAME_HEAD_CHARS = 512;
const ASSIST_EVENT_RE = /"event"\s*:\s*"assist_/;

/**
 * True for a task-channel frame carrying an assist event (a card, or a clip of
 * 0.1–1 MB), told from the frame's head without parsing it. The order feed
 * (`useTaskRouterWS`) skips these: `useAssistPool` handles them, and storing
 * one as `lastMessage` would re-render the App and parse the clip mid-car.
 * A frame whose `event` comes later reads false and is handled as today.
 */
export const isAssistFrame = (data: unknown): boolean =>
  typeof data === 'string' &&
  ASSIST_EVENT_RE.test(data.slice(0, ASSIST_FRAME_HEAD_CHARS));

export const isSupportedSchemaVersion = (version: unknown): boolean =>
  typeof version === 'string' && version.split('.')[0] === SCHEMA_MAJOR;

export type AssistPoolIgnoreReason =
  /** Not an assist pool event: the task feed's, left to useTaskRouterWS. */
  | 'not_pool_event'
  | 'unknown_event'
  | 'unsupported_schema'
  | 'malformed'
  | 'unrenderable_type'
  /** An order task is current: an order always beats an assist card. */
  | 'task_current'
  /**
   * Under `ASSIST_MIN_TIME_LEFT_MS` left at receipt (a slow or redelivered
   * assignment): no cue, no card.
   */
  | 'dropped_too_late';

export type AssistPoolRoute =
  | { action: AnyAction }
  | { ignored: AssistPoolIgnoreReason };

const POOL_EVENT_NAMES: ReadonlySet<string> = new Set(
  Object.values(AssistPoolEvents)
);

export const routeAssistPoolEvent = (
  message: IAssistEnvelope<AssistPoolData<IAssistDataBase>> | null,
  receivedAt: number,
  { hasCurrentTask = false }: { hasCurrentTask?: boolean } = {}
): AssistPoolRoute => {
  const event = message?.event;
  if (typeof event !== 'string' || !event.startsWith('assist_')) {
    return { ignored: 'not_pool_event' };
  }
  if (!POOL_EVENT_NAMES.has(event)) return { ignored: 'unknown_event' };

  const data: any = message?.data;
  if (!data || !isSupportedSchemaVersion(data.schema_version)) {
    return { ignored: 'unsupported_schema' };
  }
  if (!data.request_id) return { ignored: 'malformed' };

  if (
    !data.store_restaurant_code ||
    !message?.session_id ||
    !data.request ||
    typeof data.remaining_ms !== 'number'
  ) {
    return { ignored: 'malformed' };
  }
  if (!RENDERABLE_ASSIST_TYPES.has(data.type)) {
    return { ignored: 'unrenderable_type' };
  }
  if (!isRenderableRequest(data.type, data.request)) {
    return { ignored: 'malformed' };
  }
  // An assignment that lands after an order offer must not open a card over
  // the order; unanswered, it is simply one answer fewer.
  if (hasCurrentTask) return { ignored: 'task_current' };
  if (isAssistTooLate(data.remaining_ms)) {
    return { ignored: 'dropped_too_late' };
  }
  return {
    action: assistRequestAssigned({
      data,
      receivedAt,
      sessionId: message.session_id,
      restaurantCode: data.store_restaurant_code,
      laneId: data.lane_id,
    }),
  };
};
